import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import { PlusIcon } from '@heroicons/react/24/outline';
import api from '../../services/api.ts';
import { formatCurrency } from '../../utils/currency.ts';
import { useAuth } from '../../contexts/AuthContext.tsx';
import PageHeader from '../../components/ui/PageHeader.tsx';
import ConfirmationModal from '../../components/ConfirmationModal.tsx';
import StackCell from '../../components/ui/StackCell.tsx';
import OpenAction from '../../components/ui/OpenAction.tsx';

interface Movement {
  id: string;
  type: 'in' | 'out';
  description: string;
  amount: number;
  movementDate: string;
  accountName: string;
  categoryName: string | null;
  originLabel: string;
  origin?: string;
  referenceType?: string | null;
  referenceId?: string | null;
}

/** Resolve Abrir target: origin document when known, else list fallback. */
function resolveMovementOpenPath(m: Movement): string | null {
  const refType = m.referenceType || m.origin || '';
  const refId = m.referenceId;
  if (refId) {
    if (refType === 'receivable' || refType === 'contas_a_receber') return `/receivables/${refId}`;
    if (refType === 'payable' || refType === 'contas_a_pagar') return `/financeiro/contas-a-pagar/${refId}`;
    if (refType === 'debt' || refType === 'dividas') return `/debts/${refId}`;
    if (refType === 'ofx') return `/financeiro/conciliacao-ofx/${refId}/conciliar`;
  }
  if (m.origin === 'contas_a_receber') return '/financeiro/contas?tab=receber';
  if (m.origin === 'contas_a_pagar') return '/financeiro/contas?tab=pagar';
  if (m.origin === 'dividas') return '/financeiro/dividas';
  if (m.origin === 'ofx') return '/financeiro/conciliacao-ofx';
  if (m.origin === 'automatico') return '/financeiro/lancamentos-automaticos';
  return null;
}

interface Summary {
  periodIn: number;
  periodOut: number;
  periodBalance: number;
  accumulatedBalance: number;
  inCount: number;
  outCount: number;
  totalCount: number;
}

const defaultFrom = '2026-09-01';
const defaultTo = '2026-09-30';

const FluxoDeCaixa: React.FC = () => {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);
  const [type, setType] = useState('all');
  const [search, setSearch] = useState('');

  const { data, isLoading, error } = useQuery(
    ['cash-flow', from, to, type, search],
    async () => {
      const params: any = { from, to };
      if (type !== 'all') params.type = type;
      if (search) params.search = search;
      const res = await api.get('/financeiro/fluxo-de-caixa', { params });
      return res.data as { movements: Movement[]; summary: Summary };
    }
  );

  const summary = data?.summary;
  const movements = data?.movements || [];

  const periodLabel = useMemo(() => {
    try {
      const d = new Date(from + 'T00:00:00');
      return d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
    } catch {
      return '';
    }
  }, [from]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500" />
      </div>
    );
  }

  if (error) {
    return <div className="field-error">Erro ao carregar fluxo de caixa.</div>;
  }

  return (
    <div>
      <PageHeader
        breadcrumb="Financeiro / Fluxo de caixa"
        title="Fluxo de caixa"
        subtitle="O que já entrou/saiu"
        actions={
          hasPermission('debts.create') ? (
            <button
              type="button"
              className="btn btn-primary btn-block-mobile"
              onClick={() => navigate('/financeiro/fluxo-de-caixa/novo')}
            >
              <PlusIcon style={{ width: 18, height: 18 }} />
              Novo lançamento
            </button>
          ) : undefined
        }
      />

      <div className="toolbar">
        <div className="filters">
          <label className="filter-field">
            <span className="filter-label">De</span>
            <input type="date" className="input" value={from} onChange={e => setFrom(e.target.value)} />
          </label>
          <label className="filter-field">
            <span className="filter-label">Até</span>
            <input type="date" className="input" value={to} onChange={e => setTo(e.target.value)} />
          </label>
          <label className="filter-field">
            <span className="filter-label">Tipo</span>
            <select className="select" value={type} onChange={e => setType(e.target.value)}>
              <option value="all">Todos</option>
              <option value="in">Entradas</option>
              <option value="out">Saídas</option>
            </select>
          </label>
          <label className="filter-field" style={{ flex: 1 }}>
            <span className="filter-label">Buscar</span>
            <input
              className="input search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Descrição ou categoria"
            />
          </label>
        </div>
      </div>

      <div className="kpi-row">
        <div className="kpi">
          <div className="kpi-label">Entradas do período</div>
          <div className="kpi-value positive">{formatCurrency(summary?.periodIn || 0)}</div>
          <div className="kpi-hint">{summary?.inCount || 0} lançamentos</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Saídas do período</div>
          <div className="kpi-value negative">{formatCurrency(summary?.periodOut || 0)}</div>
          <div className="kpi-hint">{summary?.outCount || 0} lançamentos</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Saldo do período</div>
          <div className={`kpi-value ${(summary?.periodBalance || 0) >= 0 ? 'positive' : 'negative'}`}>
            {formatCurrency(summary?.periodBalance || 0)}
          </div>
          <div className="kpi-hint" style={{ textTransform: 'capitalize' }}>{periodLabel}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Saldo acumulado</div>
          <div className="kpi-value">{formatCurrency(summary?.accumulatedBalance || 0)}</div>
          <div className="kpi-hint">até {new Date(to + 'T00:00:00').toLocaleDateString('pt-BR')}</div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-title">Lançamentos</div>
          <span className="muted">{summary?.totalCount || 0} registros</span>
        </div>
        {movements.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center' }} className="muted">
            Nenhum lançamento no período. Use <strong>Novo lançamento</strong> ou registre uma baixa em Contas / Dívidas.
          </div>
        ) : (
          <div className="table-wrap">
            <table className="data-table compact-mobile">
              <thead>
                <tr>
                  <th>Lançamento</th>
                  <th className="hide-m">Data</th>
                  <th className="hide-m">Tipo</th>
                  <th className="hide-m">Categoria</th>
                  <th className="hide-m">Conta</th>
                  <th className="hide-m" style={{ textAlign: 'right' }}>Valor</th>
                  <th style={{ textAlign: 'right' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {movements.map(m => (
                  <tr key={m.id}>
                    <td>
                      <StackCell
                        title={m.description}
                        meta={m.originLabel || undefined}
                        stackExtra={
                          <>
                            {new Date(m.movementDate + 'T00:00:00').toLocaleDateString('pt-BR')}
                            {' · '}
                            {m.type === 'in' ? 'Entrada' : 'Saída'}
                            {' · '}
                            {m.categoryName || '—'} · {m.accountName}
                            {' · '}
                            {m.type === 'in' ? '+' : '−'} {formatCurrency(m.amount)}
                          </>
                        }
                      />
                    </td>
                    <td className="mono hide-m">
                      {new Date(m.movementDate + 'T00:00:00').toLocaleDateString('pt-BR')}
                    </td>
                    <td className="hide-m">
                      <span className="type-pill">
                        <span className={`dot ${m.type === 'in' ? 'dot-in' : 'dot-out'}`} />
                        {m.type === 'in' ? 'Entrada' : 'Saída'}
                      </span>
                    </td>
                    <td className="hide-m">{m.categoryName || '—'}</td>
                    <td className="hide-m">{m.accountName}</td>
                    <td className={`amount hide-m ${m.type === 'in' ? 'in' : 'out'}`} style={{ textAlign: 'right' }}>
                      {m.type === 'in' ? '+' : '−'} {formatCurrency(m.amount)}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div className="row-actions">
                        <OpenAction
                          onClick={() => {
                            const href = resolveMovementOpenPath(m);
                            if (href) navigate(href);
                          }}
                        />
                        {m.origin === 'manual' && hasPermission('debts.delete') && (
                          <button
                            type="button"
                            className="link-btn action-keep action-danger"
                            onClick={() => { setDeleteId(m.id); setShowDeleteModal(true); }}
                          >
                            Excluir
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setDeleteId(null); }}
        title="Excluir lançamento"
        message="Tem certeza que deseja excluir este lançamento manual? Esta ação não pode ser desfeita."
        onConfirm={async () => {
          if (!deleteId) return;
          setDeleting(true);
          try {
            await api.delete(`/financeiro/fluxo-de-caixa/${deleteId}`);
            queryClient.invalidateQueries(['cash-flow']);
          } finally {
            setDeleting(false);
            setShowDeleteModal(false);
            setDeleteId(null);
          }
        }}
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
        isLoading={deleting}
      />
    </div>
  );
};

export default FluxoDeCaixa;
